import { useEffect, useState } from 'react';
import {
  api, ApiError,
  type Suggestion, type SuggestResponse, type WeekPlan,
} from '../api';
import { Button, Card, ErrorNote, Sheet, Skeleton, inputClass } from '../ui';

/**
 * "What should we eat?" — the suggester, and the sheet for putting its answer
 * on a night.
 *
 * This lived inside `Ideas.tsx`, and its entry point was gated on the idea pool
 * having entries: `pool && dishes > 0`. That is not what the feature needs.
 * `suggestDinners` reads ideas, saved recipes AND frequently bought
 * ingredients, and only refuses when ideas and recipes are *both* empty
 * (`apps/api/src/ai/suggest.ts`). So once every idea had been promoted to a
 * recipe the pool emptied, the button disappeared, and a feature that still
 * worked perfectly became unreachable.
 *
 * It lives here now because it is shared: Recipes offers it, and Ideas still
 * needs `PlanSheet` for planning a bare idea.
 */

const EFFORT_LABEL: Record<Suggestion['effort'], string> = {
  quick: 'quick',
  medium: 'medium',
  project: 'a project',
};

const SOURCE_STYLE: Record<Suggestion['source'], { label: string; className: string }> = {
  your_list: { label: 'on your lists', className: 'bg-accent-soft text-accent-deep' },
  your_recipes: { label: 'saved recipe', className: 'bg-accent-soft text-accent-deep' },
  new: { label: 'new idea', className: 'bg-bg-inset text-text-muted' },
};

export function SuggestSheet({ onClose, onPlanned }: { onClose: () => void; onPlanned: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SuggestResponse | null>(null);
  const [planning, setPlanning] = useState<string | null>(null);

  async function run() {
    setBusy(true); setError(null);
    try {
      setResult(await api.post<SuggestResponse>('/ideas/suggest', {
        count: 6,
        note: note.trim() || null,
      }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not get suggestions.');
    } finally { setBusy(false); }
  }

  if (planning) {
    return (
      <PlanSheet
        dish={planning}
        onClose={() => setPlanning(null)}
        onPlanned={() => { setPlanning(null); onPlanned(); }}
      />
    );
  }

  return (
    <Sheet title="What should we eat?" onClose={onClose}>
      {!result && (
        <>
          <p className="text-sm text-text-muted">
            Reads your uploaded lists, your recipes, and what you actually buy — then suggests
            dinners that are recognizably yours.
          </p>
          <input aria-label="Dinner preferences" value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything specific? e.g. “quick, no oven”"
            className={`${inputClass} mt-4`}
          />
          {/*
            The server's own message is shown rather than a generic failure.
            An empty corpus comes back as "upload some old lists or save a
            recipe first", which tells you what to do; hiding the button instead
            told you nothing, which is how this feature went missing.
          */}
          {error && <div className="mt-3"><ErrorNote>{error}</ErrorNote></div>}
          <Button className="mt-4 w-full" disabled={busy} onClick={() => void run()}>
            {busy ? 'Thinking…' : 'Suggest dinners'}
          </Button>
        </>
      )}

      {result && (
        <>
          <p className="rounded-card bg-accent-soft px-4 py-3 text-sm leading-relaxed text-accent-deep">
            {result.read_on_the_household}
          </p>

          <ul className="mt-3 space-y-2.5">
            {result.suggestions.map((s, i) => (
              <li key={i}>
                <Card className="px-4 py-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="min-w-0 font-medium leading-snug text-text-primary">{s.dish}</h3>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${SOURCE_STYLE[s.source].className}`}>
                      {SOURCE_STYLE[s.source].label}
                    </span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-text-muted">{s.why}</p>
                  {s.key_ingredients.length > 0 && (
                    <p className="mt-1.5 text-xs text-text-muted">
                      {s.key_ingredients.join(' · ')} — {EFFORT_LABEL[s.effort]}
                    </p>
                  )}
                  <Button
                    variant="secondary"
                    className="mt-3 w-full"
                    onClick={() => setPlanning(s.dish)}
                  >
                    Put it on the week
                  </Button>
                </Card>
              </li>
            ))}
          </ul>

          <p className="mt-4 px-1 text-xs text-text-muted">
            From {result.corpusSize.ideas} of your dishes, {result.corpusSize.recipes} recipes and
            {' '}{result.corpusSize.ingredients} things you buy regularly · cost{' '}
            {result.costCents < 1 ? '<1¢' : `${Math.round(result.costCents)}¢`}
          </p>
          <Button variant="secondary" className="mt-3 w-full" disabled={busy} onClick={() => void run()}>
            {busy ? 'Thinking…' : 'Different ones'}
          </Button>
        </>
      )}
    </Sheet>
  );
}

/**
 * Drop a dish onto a day as a freetext slot. Freetext rather than a recipe
 * because that is what an idea is -- a name on a list. `meal_slots` takes a
 * recipe or free text, never both, which is exactly this distinction.
 */
export function PlanSheet({ dish, onClose, onPlanned }: {
  dish: string; onClose: () => void; onPlanned: () => void;
}) {
  const [week, setWeek] = useState<WeekPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<WeekPlan>('/plan')
      .then(setWeek)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load this week.'));
  }, []);

  async function place(date: string) {
    if (!week) return;
    setBusy(true); setError(null);
    try {
      await api.post('/plan/slots', {
        meal_plan_id: week.plan_id,
        date,
        meal_type: 'dinner',
        recipe_id: null,
        freetext: dish,
      });
      onPlanned();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not add it.');
      setBusy(false);
    }
  }

  return (
    <Sheet title={`Plan “${dish}”`} onClose={onClose}>
      {error && <ErrorNote>{error}</ErrorNote>}
      {!week && !error && <Skeleton rows={3} />}
      {week && (
        <>
          <p className="mb-3 text-sm text-text-muted">Which night?</p>
          <div className="grid grid-cols-2 gap-2">
            {week.days.map((d) => {
              const taken = week.slots.filter((s) => s.date === d);
              return (
                <button
                  key={d}
                  type="button"
                  disabled={busy}
                  onClick={() => void place(d)}
                  className="tap rounded-card border border-border-subtle bg-bg-raised px-3 py-3 text-left active:bg-accent-soft disabled:opacity-50"
                >
                  <p className="text-sm font-medium text-text-primary">{dayLabel(d)}</p>
                  <p className="mt-0.5 text-xs text-text-muted">
                    {taken[0]
                      ? (taken[0].recipe_title ?? taken[0].freetext ?? 'planned')
                      : 'empty'}
                  </p>
                </button>
              );
            })}
          </div>
        </>
      )}
    </Sheet>
  );
}

function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'short', month: 'numeric', day: 'numeric',
  });
}
